import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Textarea from '@cloudscape-design/components/textarea';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { SchedulingThread, ThreadMessage, getThread, markThreadAssignedExternally, reopenThread, sendMessage } from './api';
import { aliasFromIdToken } from './auth';
import BadgePhoto from './BadgePhoto';
import './chat.css';

interface Props {
  idToken: string;
  thread: SchedulingThread;
  onClose: () => void;
  onStatusChange: (status: SchedulingThread['status']) => void;
}

function formatMessageTime(ts?: string): string | null {
  if (!ts) return null;
  const date = new Date(ts);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ChatPanel({ idToken, thread, onClose, onStatusChange }: Props) {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [markingAssigned, setMarkingAssigned] = useState(false);
  const [markAssignedModalOpen, setMarkAssignedModalOpen] = useState(false);
  const [markAssignedNote, setMarkAssignedNote] = useState('');
  const [reopening, setReopening] = useState(false);
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState(thread.status);

  // 'discussion_ongoing' and 'reopened' are the interactive states (composer +
  // action buttons shown). Every OTHER state is treated as terminal/recoverable
  // and offers "Re-open conversation" — a safe fallback so no status (including
  // 'assignment_drifted', or any future one) can leave the operator with neither
  // a composer nor a re-open button (a dead-end thread they cannot reply to).
  const isInteractive = status === 'discussion_ongoing' || status === 'reopened';
  const isTerminal = !isInteractive;
  const busy = sending || markingAssigned || reopening;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingAbortRef = useRef<AbortController | null>(null);
  const myAlias = aliasFromIdToken(idToken);

  // Abort polling on unmount to prevent state updates on dead component
  useEffect(() => {
    return () => {
      pollingAbortRef.current?.abort();
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Optimistically appended messages get a local timestamp; the server
  // stores its own on save, which is reloaded when the thread reopens.
  const appendMessage = (msg: ThreadMessage) => {
    setMessages((prev) => [...prev, { ...msg, ts: new Date().toISOString() }]);
  };

  const loadThread = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await getThread(idToken, thread.threadUuid);
      setMessages(detail.messages);
      setStatus(detail.status);
    } finally {
      setLoading(false);
    }
  }, [idToken, thread.threadUuid]);

  useEffect(() => {
    void loadThread();
  }, [loadThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const pollForResponse = async (initialMessageCount: number) => {
    const maxAttempts = 60; // 60 × 3s = 3 minutes max
    const interval = 3000;
    // We expect: initialMessageCount (before) + 1 (user msg we sent) + 1+ (assistant response)
    const expectedMinimum = initialMessageCount + 2;

    const controller = new AbortController();
    pollingAbortRef.current = controller;

    const abortableSleep = () => new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, interval);
      controller.signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
    });

    for (let i = 0; i < maxAttempts; i++) {
      if (controller.signal.aborted) return;
      await abortableSleep();
      if (controller.signal.aborted) return;
      try {
        const detail = await getThread(idToken, thread.threadUuid);
        if (detail.messages.length >= expectedMinimum) {
          // Response arrived — show messages progressively
          const newAssistantMsgs = detail.messages.slice(initialMessageCount + 1)
            .filter((m) => m.role === 'assistant' && m.content);

          if (newAssistantMsgs.length > 0) {
            // Reveal messages one by one with a short delay for better UX
            for (let j = 0; j < newAssistantMsgs.length; j++) {
              if (j > 0) {
                await new Promise((r) => setTimeout(r, 800));
              }
              const msg = newAssistantMsgs[j];
              if (j === newAssistantMsgs.length - 1 || !msg) {
                setMessages(detail.messages);
              } else {
                // Show up to this assistant message
                const partialEnd = detail.messages.indexOf(msg) + 1;
                setMessages(detail.messages.slice(0, partialEnd));
              }
            }
          } else {
            setMessages(detail.messages);
          }

          if (detail.status !== status) {
            setStatus(detail.status);
            onStatusChange(detail.status);
          }
          return;
        }
      } catch {
        // Polling failure — continue trying
      }
    }
    // Timeout after 3 minutes
    appendMessage({ role: 'assistant', content: '⚠️ Response is taking longer than expected. Please refresh the page to check for updates.' });
  };

  const handleSendWithPolling = async (text: string, messageCountBeforeSend: number) => {
    try {
      const res = await sendMessage(idToken, thread.threadUuid, text);
      if (res.status === 'processing') {
        // Async mode: poll for the assistant response
        // messageCountBeforeSend = count before user message was appended locally
        // DDB will have: messageCountBeforeSend + 1 (user) + 1+ (assistant)
        await pollForResponse(messageCountBeforeSend);
      } else if (res.response) {
        // Backward compat: sync response
        appendMessage({ role: 'assistant', content: res.response });
        if (res.status !== status) {
          setStatus(res.status);
          onStatusChange(res.status);
        }
      }
    } catch {
      appendMessage({ role: 'assistant', content: '⚠️ Failed to send message. Please try again.' });
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || markingAssigned) return;

    const countBeforeSend = messages.length;
    setInput('');
    setSending(true);
    appendMessage({ role: 'user', content: text, sender: myAlias });

    await handleSendWithPolling(text, countBeforeSend);
    setSending(false);
  };

  const handleApprove = async () => {
    if (sending || markingAssigned) return;
    const countBeforeSend = messages.length;
    setSending(true);
    appendMessage({ role: 'user', content: '✅ Approved — please proceed.', sender: myAlias });
    await handleSendWithPolling('I approve. Please proceed with the change and give me a confirmation.', countBeforeSend);
    setSending(false);
  };

  const handleReject = async () => {
    if (sending || markingAssigned) return;
    const countBeforeSend = messages.length;
    setSending(true);
    appendMessage({ role: 'user', content: '❌ Rejected — please propose a different assignment.', sender: myAlias });
    await handleSendWithPolling('I reject this proposal. Please suggest a different assignment with different testers.', countBeforeSend);
    setSending(false);
  };

  const handleMarkAssigned = () => {
    if (sending || markingAssigned) return;
    setMarkAssignedNote('');
    setMarkAssignedModalOpen(true);
  };

  const confirmMarkAssigned = async () => {
    const note = markAssignedNote.trim();
    setMarkAssignedModalOpen(false);
    setMarkingAssigned(true);
    try {
      const result = await markThreadAssignedExternally(idToken, thread.threadUuid, note);
      appendMessage({
        role: 'assistant',
        content: `✅ Marked as assigned/handled outside ASPEN by @${myAlias}.${note ? `\n\nNote: ${note}` : ''}`,
      });
      const newStatus = (result.threadStatus || 'assigned_externally') as typeof status;
      setStatus(newStatus);
      onStatusChange(newStatus);
    } catch {
      appendMessage({ role: 'assistant', content: '⚠️ Failed to mark as assigned. Please try again.' });
    } finally {
      setMarkingAssigned(false);
    }
  };

  const handleReopen = () => {
    if (busy) return;
    setReopenModalOpen(true);
  };

  const confirmReopen = async () => {
    setReopenModalOpen(false);
    setReopening(true);
    try {
      const result = await reopenThread(idToken, thread.threadUuid);
      appendMessage({
        role: 'assistant',
        content: `🔓 Conversation re-opened by @${myAlias} to make changes.`,
      });
      const newStatus = (result.threadStatus || 'reopened') as typeof status;
      setStatus(newStatus);
      onStatusChange(newStatus);
    } catch {
      appendMessage({ role: 'assistant', content: '⚠️ Failed to re-open the conversation. Please try again.' });
    } finally {
      setReopening(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <Modal
      visible
      onDismiss={() => { pollingAbortRef.current?.abort(); onClose(); }}
      size="max"
      header={
        <Header description={thread.title}>
          {thread.avpId}
          {'  —  '}
          <Link href={`https://issues.amazon.com/issues/${thread.avpId}`} external>
            SIM Ticket
          </Link>
        </Header>
      }
    >
      <SpaceBetween size="s">
        {status === 'assigned' && (
          <StatusIndicator type="success">Assigned — conversation closed</StatusIndicator>
        )}

        {status === 'closed_ignored' && (
          <StatusIndicator type="error">Closed/Ignored — no internal tester needed</StatusIndicator>
        )}

        {status === 'cancelled' && (
          <StatusIndicator type="error">Cancelled — booking called off</StatusIndicator>
        )}

        {status === 'assigned_externally' && (
          <StatusIndicator type="info">Assigned outside ASPEN — conversation closed</StatusIndicator>
        )}

        {status === 'assignment_drifted' && (
          <StatusIndicator type="warning">
            Booking removed outside ASPEN — re-open to reschedule or close
          </StatusIndicator>
        )}

        {status === 'reopened' && (
          <StatusIndicator type="in-progress">Re-opened — conversation active again for changes</StatusIndicator>
        )}

        {isTerminal && !loading && (
          <Box>
            <Button
              iconName="undo"
              onClick={handleReopen}
              loading={reopening}
              disabled={busy}
            >
              Re-open conversation
            </Button>
          </Box>
        )}

        <div className="chat-container">
          {loading ? (
            <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>
          ) : (
            <div className="chat-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`chat-bubble chat-bubble--${msg.role}`}>
                  <div className="chat-bubble__label">
                    {msg.role === 'user' && msg.sender && msg.sender !== 'ASPEN' && (
                      <BadgePhoto alias={msg.sender} />
                    )}
                    {msg.role === 'user' ? (msg.sender || 'User') : 'ASPEN'}
                    {formatMessageTime(msg.ts) && (
                      <span className="chat-bubble__time">{formatMessageTime(msg.ts)}</span>
                    )}
                  </div>
                  <div className="chat-bubble__content"><Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{msg.content}</Markdown></div>
                </div>
              ))}
              {sending && (
                <div className="chat-bubble chat-bubble--assistant">
                  <div className="chat-bubble__label">ASPEN</div>
                  <div className="chat-bubble__content chat-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {isInteractive && !loading && (
          <div className="chat-actions-row">
            <button
              className="chat-approve-btn"
              onClick={() => { setInput(''); void handleApprove(); }}
              disabled={sending || markingAssigned}
            >
              ✅ Approve
            </button>
            <button
              className="chat-reject-btn"
              onClick={() => { setInput(''); void handleReject(); }}
              disabled={sending || markingAssigned}
            >
              ❌ Reject
            </button>
            <button
              className="chat-external-btn"
              onClick={handleMarkAssigned}
              disabled={sending || markingAssigned}
              title="Use when this pentest was already staffed/handled outside ASPEN (e.g. with vendors)"
            >
              📌 Mark as Assigned (outside ASPEN)
            </button>
          </div>
        )}

        <Modal
          visible={markAssignedModalOpen}
          onDismiss={() => setMarkAssignedModalOpen(false)}
          header="Mark as assigned outside ASPEN"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setMarkAssignedModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => void confirmMarkAssigned()}>
                  Mark as assigned
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <Box>
              This closes the conversation — no PRT/Playbook booking will be made. Use this when the pentest was
              already staffed/handled outside ASPEN (e.g. scheduled with vendors, or assigned directly elsewhere).
            </Box>
            <FormField label="Optional note" description='e.g. "scheduled with vendors", "already assigned to jeff"'>
              <Textarea
                value={markAssignedNote}
                onChange={({ detail }) => setMarkAssignedNote(detail.value)}
                placeholder="Add context for the audit trail (optional)"
                rows={3}
              />
            </FormField>
          </SpaceBetween>
        </Modal>

        <Modal
          visible={reopenModalOpen}
          onDismiss={() => setReopenModalOpen(false)}
          header="Re-open this conversation?"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setReopenModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => void confirmReopen()}>
                  Re-open
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <Box>
              This makes the conversation interactive again so you can chat with ASPEN to change the
              schedule (e.g. reschedule or cancel testers). Re-opening only unlocks the ASPEN
              conversation — it does <b>not</b> change anything in Playbook, PRT or the SIM ticket by
              itself. The thread will show as <b>Re-opened</b> until it is scheduled or closed again.
            </Box>
          </SpaceBetween>
        </Modal>

        {isInteractive && !loading && (
          <div className="chat-input-row">
            <textarea
              className="chat-input"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending || markingAssigned}
              rows={2}
            />
            <button
              className={`chat-send-btn ${sending ? 'chat-send-btn--disabled' : ''}`}
              onClick={() => void handleSend()}
              disabled={sending || markingAssigned || !input.trim()}
              aria-label="Send message"
            >
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path d="M2 21l21-9L2 3v7l15 2-15 2z" fill="currentColor" />
              </svg>
            </button>
          </div>
        )}
      </SpaceBetween>
    </Modal>
  );
}
